import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import * as Yup from "yup";
import { SingleValue, MultiValue } from "react-select";
import { AppDispatch, AppState } from "../../../redux/store";
import PermissionGate from "../../../core/layouts/permission-gate";
import ToastComponent from "../../../components/toast";
import StyledSelect from "../../../components/styled-select";
import { OptionValue } from "../../../types/component";
import Input from "../../../components/input";
import Button from "../../../components/button";
import { Formik } from "formik";
import { UserRole } from "../../../redux/features/user-slice";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../../redux/features/common-slice";
import paths from "../../../navigation/paths";
import { isSelectSingleValue } from "../../../core/utils";
import { format } from "date-fns";
import {
  getCandidateByIdState,
  candidateByIdStateAction,
  getUpdateCandidateState,
  updateCandidateStateAction,
  clearUpdateCandidateState,
  downloadCandidateCVStateAction,
  CandidateChannel,
  CandidateStatus,
  EnglishLevel,
  CandidateUpdateRequest,
} from "../../../redux/features/candidate-slice";
import {
  getAllJDsState,
  allJDsStateAction,
  JDResponse,
} from "../../../redux/features/jd-slice";
import { AnyAction } from "redux";

export default function UpdateCandidatePage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const candidateState = useSelector(getCandidateByIdState);
  const updateCandidateState = useSelector(getUpdateCandidateState);
  const downloadCVState = useSelector(
    (state: AppState) => state.candidates.downloadCandidateCVState
  );
  const allJDsState = useSelector(getAllJDsState);
  const errorState = useSelector(getErrorState);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const fetchCandidateDetail = useCallback(async () => {
    if (id) {
      await dispatch(candidateByIdStateAction({ id }));
    }
  }, [dispatch, id]);

  const fetchAllJDs = useCallback(async () => {
    await dispatch(allJDsStateAction({}));
  }, [dispatch]);

  useEffect(() => {
    fetchCandidateDetail();
    fetchAllJDs();
  }, [fetchCandidateDetail, fetchAllJDs]);

  useEffect(() => {
    if (candidateState.data) {
      dispatch(
        setBreadcrumbState([
          { id: paths.CANDIDATES, path: paths.CANDIDATES, title: "Candidates" },
          {
            id: candidateState.data.id,
            path: null,
            title: candidateState.data.name,
          },
        ])
      );
    }
  }, [candidateState.data, dispatch]);

  const handleSuccessToast = useCallback(
    (message: string, clearAction: () => AnyAction) => {
      toast(message, { type: "success" });
      setTimeout(() => {
        dispatch(clearAction());
        navigate(paths.CANDIDATES, { replace: true });
      }, 2000);
    },
    [dispatch, navigate]
  );

  useEffect(() => {
    if (updateCandidateState.data && updateCandidateState.data.success) {
      handleSuccessToast(
        updateCandidateState.data.message,
        clearUpdateCandidateState
      );
    }
  }, [updateCandidateState, handleSuccessToast]);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
      } else {
        toast(errorState.message, { type: "error" });
      }
    }
  }, [errorState, navigate]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === "application/pdf") {
      if (file.size > 5 * 1024 * 1024) {
        toast("File size exceeds 5MB limit", { type: "error" });
        setSelectedFile(null);
      } else {
        setSelectedFile(file);
      }
    } else {
      toast("Please select a valid PDF file", { type: "error" });
      setSelectedFile(null);
    }
  };

  const handleDownloadCV = useCallback(
    async (candidateId: string, fileName: string) => {
      try {
        const result = await dispatch(
          downloadCandidateCVStateAction({ id: candidateId })
        ).unwrap();
        const url = window.URL.createObjectURL(result);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName || "cv.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        toast("Failed to download CV. Please try again.", { type: "error" });
      }
    },
    [dispatch]
  );

  const submitUpdateCandidate = async (values: CandidateUpdateRequest) => {
    if (id) {
      const formData = new FormData();
      if (values.name) formData.append("name", values.name);
      if (values.email) formData.append("email", values.email);
      if (values.phone) formData.append("phone", values.phone);
      if (values.socialLink) formData.append("socialLink", values.socialLink);
      if (values.channel) formData.append("channel", values.channel);
      if (values.status) formData.append("status", values.status);
      if (values.currentSalary !== undefined)
        formData.append("currentSalary", values.currentSalary.toString());
      if (values.expectedSalary !== undefined)
        formData.append("expectedSalary", values.expectedSalary.toString());
      if (values.jdId) formData.append("jdId", values.jdId);
      if (values.message) formData.append("message", values.message);
      if (values.certificate)
        formData.append("certificate", values.certificate);
      if (values.englishLevel)
        formData.append("englishLevel", values.englishLevel);
      if (values.applyDate)
        formData.append("applyDate", values.applyDate.toISOString());
      if (selectedFile) formData.append("cvFile", selectedFile);

      await dispatch(updateCandidateStateAction({ id, request: formData }));
    }
  };

  const jdOptions: OptionValue[] = allJDsState.data.map((jd: JDResponse) => ({
    value: jd.id,
    label: jd.title,
  }));

  return (
    <PermissionGate
      categories={[]}
      roles={[UserRole.Admin, UserRole.Contributor]}
    >
      <Helmet
        title={
          candidateState.data
            ? `Update ${candidateState.data.name}`
            : "Update Candidate"
        }
      />
      {candidateState.data && (
        <Formik
          initialValues={
            {
              name: candidateState.data.name || "",
              email: candidateState.data.email || "",
              phone: candidateState.data.phone || "",
              socialLink: candidateState.data.socialLink || "",
              channel: candidateState.data.channel || CandidateChannel.Other,
              status: candidateState.data.status || CandidateStatus.Received,
              currentSalary: candidateState.data.currentSalary || 0,
              expectedSalary: candidateState.data.expectedSalary || 0,
              jdId: candidateState.data.jd.id || "",
              message: candidateState.data.message || "",
              certificate: candidateState.data.certificate || "",
              englishLevel:
                candidateState.data.englishLevel || EnglishLevel.Beginner,
              applyDate: candidateState.data.applyDate
                ? new Date(candidateState.data.applyDate)
                : new Date(),
            } as CandidateUpdateRequest
          }
          validationSchema={Yup.object().shape({
            name: Yup.string()
              .max(100, "Name cannot exceed 100 characters")
              .optional(),
            email: Yup.string().email("Invalid email").optional(),
            phone: Yup.string()
              .max(15, "Phone cannot exceed 15 characters")
              .optional(),
            socialLink: Yup.string().url("Invalid URL").optional(),
            channel: Yup.string().optional(),
            status: Yup.string().optional(),
            currentSalary: Yup.number()
              .min(0, "Current salary cannot be negative")
              .optional(),
            expectedSalary: Yup.number()
              .min(0, "Expected salary cannot be negative")
              .optional(),
            jdId: Yup.string().optional(),
            message: Yup.string().optional(),
            certificate: Yup.string().optional(),
            englishLevel: Yup.string().optional(),
            applyDate: Yup.date().optional(),
          })}
          onSubmit={async (values, { setSubmitting }) => {
            await submitUpdateCandidate(values);
            setSubmitting(false);
          }}
        >
          {({
            values,
            errors,
            handleChange,
            handleSubmit,
            isSubmitting,
            setValues,
            dirty,
          }) => (
            <form
              noValidate
              autoComplete="off"
              onSubmit={handleSubmit}
              className="h-full overflow-y-auto"
              encType="multipart/form-data"
            >
              <div
                className="h-full grid grid-cols-1 sm:grid-cols-2 gap-8 overflow-y-scroll"
                style={{
                  height: "calc(100% - 56px)",
                }}
              >
                <div className="space-y-4">
                  <Input
                    label="Name"
                    type="text"
                    id="name"
                    value={values.name}
                    onChange={handleChange}
                    errorText={errors.name}
                  />
                  <Input
                    label="Email"
                    type="email"
                    id="email"
                    value={values.email}
                    onChange={handleChange}
                    errorText={errors.email}
                  />
                  <Input
                    label="Phone"
                    type="text"
                    id="phone"
                    value={values.phone}
                    onChange={handleChange}
                    errorText={errors.phone}
                  />
                  <Input
                    label="Social Link"
                    type="text"
                    id="socialLink"
                    value={values.socialLink}
                    onChange={handleChange}
                    errorText={errors.socialLink}
                  />
                  <div className="flex flex-col">
                    <label className="text-sm text-text-title">Change CV</label>
                    <input
                      type="file"
                      id="cvFile"
                      accept="application/pdf"
                      onChange={handleFileChange}
                      className="border border-divider rounded-lg p-2"
                    />
                    {selectedFile && (
                      <p className="text-sm text-text-title">
                        Selected: {selectedFile.name}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm text-text-title">Channel</label>
                    <StyledSelect
                      options={Object.values(CandidateChannel).map(
                        (channel) => ({
                          value: channel,
                          label:
                            channel.charAt(0).toUpperCase() + channel.slice(1),
                        })
                      )}
                      isClearable={false}
                      menuPosition="fixed"
                      value={
                        values.channel
                          ? {
                              value: values.channel,
                              label:
                                values.channel.charAt(0).toUpperCase() +
                                values.channel.slice(1),
                            }
                          : undefined
                      }
                      onChange={(
                        value:
                          | SingleValue<OptionValue>
                          | MultiValue<OptionValue>
                      ) => {
                        if (value && isSelectSingleValue(value)) {
                          setValues({
                            ...values,
                            channel: value.value as CandidateChannel,
                          });
                        }
                      }}
                    />
                    {errors.channel && (
                      <p className="text-error-main text-sm">
                        {errors.channel}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm text-text-title">Status</label>
                    <StyledSelect
                      options={Object.values(CandidateStatus).map((status) => ({
                        value: status,
                        label: status.charAt(0).toUpperCase() + status.slice(1),
                      }))}
                      isClearable={false}
                      menuPosition="fixed"
                      value={
                        values.status
                          ? {
                              value: values.status,
                              label:
                                values.status.charAt(0).toUpperCase() +
                                values.status.slice(1),
                            }
                          : undefined
                      }
                      onChange={(
                        value:
                          | SingleValue<OptionValue>
                          | MultiValue<OptionValue>
                      ) => {
                        if (value && isSelectSingleValue(value)) {
                          setValues({
                            ...values,
                            status: value.value as CandidateStatus,
                          });
                        }
                      }}
                    />
                    {errors.status && (
                      <p className="text-error-main text-sm">{errors.status}</p>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm text-text-title">
                      English Level
                    </label>
                    <StyledSelect
                      options={Object.values(EnglishLevel).map((level) => ({
                        value: level,
                        label: level.charAt(0).toUpperCase() + level.slice(1),
                      }))}
                      isClearable={false}
                      menuPosition="fixed"
                      value={
                        values.englishLevel
                          ? {
                              value: values.englishLevel,
                              label:
                                values.englishLevel.charAt(0).toUpperCase() +
                                values.englishLevel.slice(1),
                            }
                          : undefined
                      }
                      onChange={(
                        value:
                          | SingleValue<OptionValue>
                          | MultiValue<OptionValue>
                      ) => {
                        if (value && isSelectSingleValue(value)) {
                          setValues({
                            ...values,
                            englishLevel: value.value as EnglishLevel,
                          });
                        }
                      }}
                    />
                    {errors.englishLevel && (
                      <p className="text-error-main text-sm">
                        {errors.englishLevel}
                      </p>
                    )}
                  </div>
                </div>
                <div className="space-y-4">
                  <Input
                    label="Current Salary"
                    type="text"
                    id="currentSalary"
                    value={
                      values.currentSalary
                        ? Number(values.currentSalary).toLocaleString("en-US", {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })
                        : ""
                    }
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/,/g, "");
                      handleChange({
                        target: {
                          id: "currentSalary",
                          value: rawValue ? parseInt(rawValue, 10) : "",
                        },
                      });
                    }}
                    errorText={errors.currentSalary}
                  />

                  <Input
                    label="Expected Salary"
                    type="text"
                    id="expectedSalary"
                    value={
                      values.expectedSalary
                        ? Number(values.expectedSalary).toLocaleString(
                            "en-US",
                            {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }
                          )
                        : ""
                    }
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/,/g, "");
                      handleChange({
                        target: {
                          id: "expectedSalary",
                          value: rawValue ? parseInt(rawValue, 10) : "",
                        },
                      });
                    }}
                    errorText={errors.expectedSalary}
                  />
                  <div className="flex flex-col">
                    <label className="text-sm text-text-title">
                      Job Description
                    </label>
                    <StyledSelect
                      options={jdOptions}
                      isClearable={false}
                      menuPosition="fixed"
                      loading={allJDsState.loading}
                      value={
                        values.jdId
                          ? jdOptions.find(
                              (option) => option.value === values.jdId
                            )
                          : undefined
                      }
                      onChange={(
                        value:
                          | SingleValue<OptionValue>
                          | MultiValue<OptionValue>
                      ) => {
                        if (value && isSelectSingleValue(value)) {
                          setValues({
                            ...values,
                            jdId: value.value as string,
                          });
                        }
                      }}
                    />
                    {errors.jdId && (
                      <p className="text-error-main text-sm">{errors.jdId}</p>
                    )}
                  </div>
                  <Input
                    label="Message"
                    inputType="textarea"
                    type="text"
                    id="message"
                    value={values.message}
                    onChange={handleChange}
                    errorText={errors.message}
                  />
                  <Input
                    label="Certificate"
                    type="text"
                    id="certificate"
                    value={values.certificate}
                    onChange={handleChange}
                    errorText={errors.certificate}
                  />
                  <div className="flex flex-col">
                    <label className="text-sm text-text-title">
                      Apply Date
                    </label>
                    <input
                      type="date"
                      id="applyDate"
                      value={
                        values.applyDate
                          ? format(values.applyDate, "yyyy-MM-dd")
                          : ""
                      }
                      onChange={(e) => {
                        setValues({
                          ...values,
                          applyDate: e.target.value
                            ? new Date(e.target.value)
                            : undefined,
                        });
                      }}
                      className="border border-divider rounded-lg p-2"
                    />
                    {errors.applyDate && (
                      <p className="text-error-main text-sm">
                        {errors.applyDate as string}
                      </p>
                    )}

                    {/* ---------- CV ACTIONS (Download + View) ---------- */}
                    {candidateState.data?.cVLink ? (
                      <div className="mt-4 flex gap-3">
                        {/* DOWNLOAD */}
                        <button
                          className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
                          onClick={() => {
                            if (candidateState.data) {
                              handleDownloadCV(
                                candidateState.data.id,
                                candidateState.data.cVLink?.split("/").pop() ||
                                  "cv.pdf"
                              );
                            }
                          }}
                          disabled={downloadCVState.loading}
                        >
                          {downloadCVState.loading
                            ? "Downloading..."
                            : "Download CV"}
                        </button>

                        {/* VIEW (new button) */}
                        <button
                          className="bg-green-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-green-700 transition-colors duration-200 disabled:opacity-50"
                          onClick={async () => {
                            if (candidateState.data) {
                              try {
                                const result = await dispatch(
                                  downloadCandidateCVStateAction({
                                    id: candidateState.data.id,
                                  })
                                ).unwrap();
                                const url = window.URL.createObjectURL(result);
                                window.open(url, "_blank");
                                setTimeout(
                                  () => window.URL.revokeObjectURL(url),
                                  1000
                                );
                              } catch {
                                toast("Failed to view CV. Please try again.", {
                                  type: "error",
                                });
                              }
                            }
                          }}
                          disabled={downloadCVState.loading}
                        >
                          {downloadCVState.loading ? "Loading..." : "View CV"}
                        </button>
                      </div>
                    ) : (
                      <p className="text-sm text-text-title mt-2">
                        No CV available
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-4 pt-4">
                <Button
                  label="Save Changes"
                  type="submit"
                  loading={updateCandidateState.loading}
                  disabled={isSubmitting || updateCandidateState.loading}
                />
              </div>
            </form>
          )}
        </Formik>
      )}
      <ToastComponent id="updateCandidate" />
    </PermissionGate>
  );
}
